/** Same rules as the event player's `playback-sync` helper, so Current sits where the rooms are. */

export type FileVideoTarget =
  | { state: 'waiting'; waitMs: number }
  | { state: 'playing'; positionSec: number }
  | { state: 'ended' };

export function resolveFileVideoTarget(input: {
  startedAtMs: number;
  nowMs: number;
  durationSec?: number;
  loop?: boolean;
}): FileVideoTarget {
  const elapsedSec = (input.nowMs - input.startedAtMs) / 1000;
  if (elapsedSec < 0) {
    return { state: 'waiting', waitMs: -elapsedSec * 1000 };
  }
  const durationSec = input.durationSec;
  if (durationSec == null || !Number.isFinite(durationSec) || durationSec <= 0 || elapsedSec < durationSec) {
    return { state: 'playing', positionSec: elapsedSec };
  }
  if (input.loop) {
    return { state: 'playing', positionSec: elapsedSec % durationSec };
  }
  return { state: 'ended' };
}

const SYNC_SEEK_THRESHOLD_SEC = 0.25;
const SYNC_TOLERANCE_SEC = 0.04;
const SYNC_RATE_STEP = 0.01;

export type FileVideoDriftCorrection = { kind: 'seek'; positionSec: number } | { kind: 'rate'; rate: number };

export function resolveFileVideoDriftCorrection(
  currentSec: number,
  targetSec: number,
  loopDurationSec?: number,
): FileVideoDriftCorrection {
  let driftSec = currentSec - targetSec;
  // A looping element wraps on its own; just past the boundary it is ahead, not a full clip behind.
  if (loopDurationSec != null && Number.isFinite(loopDurationSec) && loopDurationSec > 0) {
    if (driftSec > loopDurationSec / 2) {
      driftSec -= loopDurationSec;
    } else if (driftSec < -loopDurationSec / 2) {
      driftSec += loopDurationSec;
    }
  }
  if (Math.abs(driftSec) > SYNC_SEEK_THRESHOLD_SEC) {
    return { kind: 'seek', positionSec: targetSec };
  }
  if (Math.abs(driftSec) <= SYNC_TOLERANCE_SEC) {
    return { kind: 'rate', rate: 1 };
  }
  return { kind: 'rate', rate: driftSec > 0 ? 1 - SYNC_RATE_STEP : 1 + SYNC_RATE_STEP };
}

export function readPlaybackStartedAtMs(value: string | undefined): number | undefined {
  if (!value) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}
